/**
 * The CRM audit ledger, from the browser's side.
 *
 * Its own file rather than a corner of `clients.ts`: that one is already past
 * the file-size line this repo keeps (AGENTS.md §4), and the ledger is not a
 * client concept. It is read from two places that have nothing else in common -
 * the Settings screen and one client's Logs tab - which is exactly the shape
 * that belongs in a module of its own.
 *
 * Server side: `apps/api/src/modules/crm-audit/`.
 */
import { api } from "./api.js";

/** "user" - a signed-in colleague. Anything else had no session. */
export type CrmLogActorType = "user" | "system" | "portal" | "webhook";

export interface CrmLogRow {
  id: string;
  /** ISO. */
  at: string;
  /** Dotted and past tense: "client.updated", "invoice.sent". */
  action: string;
  actorType: string;
  actorUserId: string | null;
  /** The colleague's name as it stood when the row was written. */
  actorName: string | null;
  entityType: string | null;
  entityId: string | null;
  /** An invoice number, a subject line, a title. */
  entityLabel: string | null;
  clientId: string | null;
  /**
   * Resolved by the server for the ids on this page. Null means the client is
   * GONE, not that the row lost its link - the ledger keeps history that
   * outlives the record it is about.
   */
  clientName: string | null;
  leadId: string | null;
  projectId: string | null;
  /** {before, after} on an update, or null. */
  meta: unknown;
  ip: string | null;
}

export interface CrmLogPage {
  rows: CrmLogRow[];
  total: number;
  page: number;
  pageSize: number;
}

export interface CrmLogFacets {
  /** How far back the counts below were taken. Printed, so the numbers are honest. */
  windowDays: number;
  modules: { key: string; count: number }[];
  actions: { key: string; count: number }[];
  actors: { id: string; name: string | null; count: number }[];
}

export interface CrmLogQuery {
  page?: number;
  pageSize?: number;
  q?: string;
  module?: string;
  action?: string;
  actorUserId?: string;
  entityType?: string;
  entityId?: string;
  clientId?: string;
  /** YYYY-MM-DD. The server treats `to` as the whole of that day. */
  from?: string;
  to?: string;
}

function qs(params?: Record<string, string | number | undefined>): string {
  if (!params) return "";
  const p = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== "")
    .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`);
  return p.length ? `?${p.join("&")}` : "";
}

export const listCrmLogs = (params?: CrmLogQuery) =>
  api.get<CrmLogPage>(`/admin/crm/logs${qs(params as Record<string, string | number | undefined>)}`);

export const getCrmLogFacets = () => api.get<CrmLogFacets>("/admin/crm/logs/facets");

export const getCrmLogStaff = () =>
  api.get<{ rows: { id: string; name: string }[] }>("/admin/crm/logs/staff");

// ── One client's log ────────────────────────────────────────────────────────

/** Where the entry came from: the ledger, or the mailbox. */
export type ClientLogSource = "audit" | "mail";

export interface ClientLogRow {
  id: string;
  source: ClientLogSource;
  at: string;
  action: string;
  actorType: string;
  actorName: string | null;
  title: string;
  subtitle: string | null;
  entityType: string | null;
  entityId: string | null;
  /** "inbound" | "outbound" on a letter, null on a ledger row. */
  direction: string | null;
  /** The other party's address on a letter. */
  counterpart: string | null;
  meta: unknown;
  link: string | null;
}

export interface ClientLogPage {
  rows: ClientLogRow[];
  total: number;
  /** Keyed "audit" | "in" | "out" - what the filter chips count. */
  counts: Record<string, number>;
}

export const getClientLog = (clientId: string, params?: { limit?: number }) =>
  api.get<ClientLogPage>(
    `/admin/crm/clients/${clientId}/logs${qs(params as Record<string, string | number | undefined>)}`,
  );
